import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { getUserOrgId } from "../getUserOrgId";
import getNetworkSite from "../http/getNetworkSite";
import getNetworkSiteList from "../http/getNetworkSiteList";
import { NetworkSite } from "../models/site";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

const useNetworkSiteList = (
  orgId: string | undefined,
  enabled: boolean,
  onlyLinkedNetwork: boolean,
  userOrgId?: string
) => {
  const { getToken, getClaim } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  const fetchNetworkSiteList = useQuery<
    {
      sites: (NetworkSite | null)[];
    },
    Error
  >(
    ["org", orgId, "networksites"],
    async () => {
      return getNetworkSiteList(
        await getAuthTokenBlocking(getToken, relogin),
        orgId!,
        onlyLinkedNetwork,
        await getUserOrgId(getClaim, userOrgId)
      );
    },
    { enabled: enabled && !!orgId, staleTime: 60_000 }
  );

  const chunk = <T>(arr: T[], chunkSize: number): T[][] => {
    const chunks: T[][] = [];
    const tmp = [...arr];
    while (tmp.length) {
      chunks.push(tmp.splice(0, chunkSize));
    }
    return chunks;
  };

  const useNetworkSiteData = (sitesIds: string[]) => {
    return useQueries({
      queries: chunk(sitesIds, 10)
        .map((networkSiteIdsChunk) => networkSiteIdsChunk.join(","))
        .map((networkSiteIdsChunkStr) => {
          return {
            queryKey: ["org", orgId, "networksites", networkSiteIdsChunkStr],
            queryFn: async () =>
              getNetworkSite(
                await getAuthTokenBlocking(getToken, relogin),
                orgId!,
                networkSiteIdsChunkStr,
                await getUserOrgId(getClaim, userOrgId)
              ),
            staleTime: 5 * 60 * 1000,
            enabled: enabled && !!orgId,
          };
        }),
    });
  };

  const networkSites = (fetchNetworkSiteList.data?.sites || []).filter(
    (site) => !!site
  );

  return {
    fetchNetworkSiteList,
    networkSites,
    useNetworkSiteData,
  };
};

export default useNetworkSiteList;
